import { Channel, channelFriendlyNames } from '../contracts/constants';
import { IPolicies } from './types';

export enum CancellationPreferenceTypes {
	FLEXIBLE = 'flexible',
	MODERATE = 'moderate',
	STRICT = 'strict',
	FLEXIBLE_NON_REFUNDABLE_V1 = 'flexible_non_refundable_v1',
	MODERATE_NON_REFUNDABLE_V1 = 'moderate_non_refundable_v1',
	STRICT_NON_REFUNDABLE_V1 = 'strict_non_refundable_v1',
}

export const CancellationPreferences = {
	[CancellationPreferenceTypes.STRICT]: 'Strict',
	[CancellationPreferenceTypes.MODERATE]: 'Moderate',
	[CancellationPreferenceTypes.FLEXIBLE]: 'Flexible',
	[CancellationPreferenceTypes.FLEXIBLE_NON_REFUNDABLE_V1]: 'Flexible Non-Refundable',
	[CancellationPreferenceTypes.MODERATE_NON_REFUNDABLE_V1]: 'Moderate Non-Refundable',
	[CancellationPreferenceTypes.STRICT_NON_REFUNDABLE_V1]: 'Strict Non-Refundable',
};

export const defaultPolicies: IPolicies = {
	[Channel.AIRBNB]: {
		id: Channel.AIRBNB,
		active: true,
		teesandcees: 'https://www.airbnb.co.uk/terms',
		commission: 'default_rate',
		faqs: [
			{
				title: 'What happens when I get a booking?',
				text: 'When you receive a booking from this channel, it is automatically accepted.'
			},
			{
				title: 'What happens if a guest cancels their booking?',
				text: 'Cancellations will be subject to the rules shown on the right.'
			},
			{
				title: `Where are the ${channelFriendlyNames[Channel.AIRBNB]} terms and conditions?`,
				text: 'You can read the terms and conditions here.',
				link: 'https://www.airbnb.co.uk/terms'
			},
			{
				title: 'What happens when I get a booking for longer than 28 days?',
				subText: 'When you receive a booking that is longer than 28 days, the Long Term cancellation policy is automatically applied. Reservations are fully refundable for 48 hours after the booking is confirmed, as long as the cancellation occurs at least 28 days before check-in.',
				text: 'You can review the full information here.',
				link: 'https://www.airbnb.co.uk/home/cancellation_policies#long-term'
			},
			{
				title: 'How does the Strict policy change if my property is in Italy?',
				subText: 'If your property is in Italy then your Strict cancellation policy is slightly different. Cancel up to 30 days before your trip and get a full refund, including all fees. Cancel within 30 days of the trip and get a 50% refund of the total nightly rate, as well as a full refund of fees.',
				text: 'You can review the full information here.',
				link: 'https://www.airbnb.co.uk/home/updated_cancellation_policies#additional-terms'
			}
		],
		policies: {
			[CancellationPreferenceTypes.STRICT]: {
				name: 'betterStrictWithGracePeriod',
				refund: [
					{
						amount: 100,
						period: 'within 48 hours of booking and at least 30 days before arrival'
					},
					{
						amount: 50,
						period: 'up to 7 days before arrival'
					},
					{
						amount: 0,
						period: 'within 7 days of arrival'
					}
				],
				guestPays: [
					{
						amount: 100,
						period: 'at time of booking'
					}
				],
				ownerPaid: [
					{
						period: 'within 3-10 days of guest arrival'
					}
				]
			},
			[CancellationPreferenceTypes.MODERATE]: {
				name: 'moderate',
				refund: [
					{
						amount: 100,
						period: 'Up to 5 days before arrival if booked more than 14 days before arrival, or within 48 hours after booking. Post that up to 5 days before booking less service fees.'
					},
					{
						amount: 50,
						period: 'Within 5 days of arrival and cleaning fee refunded.'
					}
				],
				guestPays: [
					{
						amount: 100,
						period: 'at time of booking'
					}
				],
				ownerPaid: [
					{
						period: 'within 3-10 days of guest arrival'
					}
				]
			},
			[CancellationPreferenceTypes.FLEXIBLE]: {
				name: 'moderate',
				refund: [
					{
						amount: 100,
						period: 'Up to 5 days before arrival if booked more than 14 days before arrival, or within 48 hours after booking. Post that up to 5 days before booking less service fees.'
					},
					{
						amount: 50,
						period: 'Within 5 days of arrival and cleaning fee refunded.'
					}
				],
				guestPays: [
					{
						amount: 100,
						period: 'at time of booking'
					}
				],
				ownerPaid: [
					{
						period: 'within 3-10 days of guest arrival'
					}
				]
			}
		}
	},
	[Channel.BOOKINGCOM]: {
		id: Channel.BOOKINGCOM,
		active: true,
		teesandcees: 'https://www.booking.com/content/terms.html',
		commission: 'bookingcom_rate',
		faqs: [
			{
				title: `Where are the ${channelFriendlyNames[Channel.RENTALS_UNITED_BOOKINGCOM]} terms and conditions?`,
				text: 'You can read the terms and conditions here.',
				link: 'https://www.booking.com/content/terms.html'
			}
		],
		policies: {
			[CancellationPreferenceTypes.STRICT]: {
				name: 'bookingcom_30',
				refund: [
					{
						amount: 100,
						period: 'up to 30 days before arrival'
					}
				],
				guestPays: [
					{
						amount: 100,
						period: '30 days before arrival'
					}
				],
				ownerPaid: [
					{
						period: 'within 3-10 days of guest arrival'
					}
				]
			},
			[CancellationPreferenceTypes.MODERATE]: {
				name: 'bookingcom_14',
				refund: [
					{
						amount: 100,
						period: 'up to 14 days before arrival'
					}
				],
				guestPays: [
					{
						amount: 100,
						period: '14 days before arrival'
					}
				],
				ownerPaid: [
					{
						period: 'within 3-10 days of guest arrival'
					}
				]
			},
			[CancellationPreferenceTypes.FLEXIBLE]: {
				name: 'bookingcom_5',
				refund: [
					{
						amount: 100,
						period: 'up to 5 days before arrival'
					}
				],
				guestPays: [
					{
						amount: 100,
						period: '5 days before arrival'
					}
				],
				ownerPaid: [
					{
						period: 'within 3-10 days of guest arrival'
					}
				]
			}
		}
	},
	[Channel.EXPEDIA]: {
		id: Channel.EXPEDIA,
		active: true,
		teesandcees: 'https://www.expedia.co.uk/p/support/termsofuse',
		commission: 'default_rate',
		faqs: [
			{
				title: `Where are the ${channelFriendlyNames[Channel.EXPEDIA]} terms and conditions?`,
				text: 'You can read the terms and conditions here.',
				link: 'https://www.expedia.co.uk/p/support/termsofuse'
			}
		],
		policies: {
			[CancellationPreferenceTypes.STRICT]: {
				name: 'tn-strict',
				refund: [
					{
						amount: 100,
						period: 'up to 28 days in advance'
					},
					{
						amount: 0,
						period: 'within 28 days of arrival'
					}
				],
				guestPays: [
					{
						amount: 100,
						period: 'at time of booking'
					}
				],
				ownerPaid: [
					{
						period: 'within 3-10 days of guest arrival'
					}
				]
			},
			[CancellationPreferenceTypes.MODERATE]: {
				name: 'tn-moderate',
				refund: [
					{
						amount: 100,
						period: 'up to 14 days in advance'
					},
					{
						amount: 0,
						period: 'within 14 days of arrival'
					}
				],
				guestPays: [
					{
						amount: 100,
						period: 'at time of booking'
					}
				],
				ownerPaid: [
					{
						period: 'within 3-10 days of guest arrival'
					}
				]
			},
			[CancellationPreferenceTypes.FLEXIBLE]: {
				name: 'tn-flexible',
				refund: [
					{
						amount: 100,
						period: 'up to 5 days in advance'
					},
					{
						amount: 0,
						period: 'within 5 days of arrival'
					}
				],
				guestPays: [
					{
						amount: 100,
						period: 'at time of booking'
					}
				],
				ownerPaid: [
					{
						period: 'within 3-10 days of guest arrival'
					}
				]
			}
		},
	},
	[Channel.HOLIDU]: {
		id: Channel.HOLIDU,
		active: true,
		teesandcees: 'https://www.holidu.co.uk/gtc',
		commission: 'default_rate',
		faqs: [
			{
				title: `Where are the ${channelFriendlyNames[Channel.HOLIDU]} terms and conditions?`,
				text: 'You can read the terms and conditions here.',
				link: 'https://www.holidu.co.uk/gtc'
			}
		],
		policies: {
			[CancellationPreferenceTypes.STRICT]: {
				name: 'holidu_strict_v1',
				refund: [
					{
						amount: 100,
						period: 'up to 30 days before arrival'
					}
				],
				guestPays: [
					{
						amount: 100,
						period: 'at time of booking'
					}
				],
				ownerPaid: [
					{
						period: 'within 3-10 days of guest arrival'
					}
				]
			},
			[CancellationPreferenceTypes.MODERATE]: {
				name: 'holidu_moderate_v1',
				refund: [
					{
						amount: 100,
						period: 'up to 14 days before arrival'
					}
				],
				guestPays: [
					{
						amount: 100,
						period: 'at time of booking'
					}
				],
				ownerPaid: [
					{
						period: 'within 3-10 days of guest arrival'
					}
				]
			},
			[CancellationPreferenceTypes.FLEXIBLE]: {
				name: 'holidu_flexible_v1',
				refund: [
					{
						amount: 100,
						period: 'up to 5 days before arrival'
					}
				],
				guestPays: [
					{
						amount: 100,
						period: 'at time of booking'
					}
				],
				ownerPaid: [
					{
						period: 'within 3-10 days of guest arrival'
					}
				]
			}
		}
	},
	[Channel.HOMEAWAY]: {
		id: Channel.HOMEAWAY,
		active: true,
		teesandcees: 'https://www.homeaway.co.uk/info/about-us/legal/terms-conditions',
		commission: 'default_rate',
		faqs: [
			{
				title: `Where are the ${channelFriendlyNames[Channel.HOMEAWAY]} terms and conditions?`,
				text: 'You can read the terms and conditions here.',
				link: 'https://www.homeaway.co.uk/info/about-us/legal/terms-conditions'
			}
		],
		policies: {
			[CancellationPreferenceTypes.STRICT]: {
				name: 'MODERATE',
				refund: [
					{
						amount: 100,
						period: 'up to 30 days before arrival'
					},
					{
						amount: 50,
						period: 'up to 14 days before arrival'
					}
				],
				guestPays: [
					{
						amount: 100,
						period: 'at time of booking'
					}
				],
				ownerPaid: [
					{
						period: 'within 3-10 days of guest arrival'
					}
				]
			},
			[CancellationPreferenceTypes.MODERATE]: {
				name: 'MODERATE',
				refund: [
					{
						amount: 100,
						period: 'up to 5 days before arrival'
					}
				],
				guestPays: [
					{
						amount: 100,
						period: 'at time of booking'
					}
				],
				ownerPaid: [
					{
						period: 'within 3-10 days of guest arrival'
					}
				]
			},
			[CancellationPreferenceTypes.FLEXIBLE]: {
				name: 'RELAXED',
				refund: [
					{
						amount: 100,
						period: 'up to 5 days before arrival'
					}
				],
				guestPays: [
					{
						amount: 100,
						period: 'at time of booking'
					}
				],
				ownerPaid: [
					{
						period: 'within 3-10 days of guest arrival'
					}
				]
			}
		}
	},
	[Channel.HOMETOGO]: {
		id: Channel.HOMETOGO,
		active: true,
		teesandcees: 'https://www.hometogo.com/legal/#gtc',
		commission: 'default_rate',
		faqs: [
			{
				title: `Where are the ${channelFriendlyNames[Channel.HOMETOGO]} terms and conditions?`,
				text: 'You can read the terms and conditions here.',
				link: 'https://www.hometogo.com/legal/#gtc'
			}
		],
		policies: {
			[CancellationPreferenceTypes.STRICT]: {
				name: 'MODERATE',
				refund: [
					{
						amount: 100,
						period: 'up to 30 days before arrival'
					},
					{
						amount: 50,
						period: 'up to 14 days before arrival'
					}
				],
				guestPays: [
					{
						amount: 100,
						period: 'at time of booking'
					}
				],
				ownerPaid: [
					{
						period: 'within 3-10 days of guest arrival'
					}
				]
			},
			[CancellationPreferenceTypes.MODERATE]: {
				name: 'MODERATE',
				refund: [
					{
						amount: 100,
						period: 'up to 14 days before arrival'
					}
				],
				guestPays: [
					{
						amount: 100,
						period: 'at time of booking'
					}
				],
				ownerPaid: [
					{
						period: 'within 3-10 days of guest arrival'
					}
				]
			},
			[CancellationPreferenceTypes.FLEXIBLE]: {
				name: 'RELAXED',
				refund: [
					{
						amount: 100,
						period: 'up to 5 days before arrival'
					}
				],
				guestPays: [
					{
						amount: 100,
						period: 'at time of booking'
					}
				],
				ownerPaid: [
					{
						period: 'within 3-10 days of guest arrival'
					}
				]
			}
		}
	},
	[Channel.TRIPADVISOR]: {
		id: Channel.TRIPADVISOR,
		active: true,
		teesandcees: 'https://tripadvisor.mediaroom.com/uk-terms-of-use',
		commission: 'default_rate',
		faqs: [
			{
				title: `Where are the ${channelFriendlyNames[Channel.TRIPADVISOR]} terms and conditions?`,
				text: 'You can read the terms and conditions here.',
				link: 'https://tripadvisor.mediaroom.com/uk-terms-of-use'
			}
		],
		policies: {
			[CancellationPreferenceTypes.FLEXIBLE]: {
				name: 'RELAXED',
				refund: [
					{
						amount: 100,
						period: 'up to 14 days before arrival'
					},
					{
						amount: 0,
						period: 'within 14 days of arrival'
					}
				],
				guestPays: [
					{
						amount: 25,
						period: 'at time of booking'
					},
					{
						amount: 75,
						period: '60 days before arrival'
					}
				],
				ownerPaid: [
					{
						period: 'within 3-10 days of guest arrival'
					}
				]
			},
			[CancellationPreferenceTypes.MODERATE]: {
				name: 'RELAXED',
				refund: [
					{
						amount: 100,
						period: 'up to 14 days before arrival'
					},
					{
						amount: 0,
						period: 'within 14 days of arrival'
					}
				],
				guestPays: [
					{
						amount: 25,
						period: 'at time of booking'
					},
					{
						amount: 75,
						period: '60 days before arrival'
					}
				],
				ownerPaid: [
					{
						period: 'within 3-10 days of guest arrival'
					}
				]
			},
			[CancellationPreferenceTypes.STRICT]: {
				name: 'RELAXED',
				refund: [
					{
						amount: 100,
						period: 'up to 14 days before arrival'
					},
					{
						amount: 0,
						period: 'within 14 days of arrival'
					}
				],
				guestPays: [
					{
						amount: 25,
						period: 'at time of booking'
					},
					{
						amount: 75,
						period: '60 days before arrival'
					}
				],
				ownerPaid: [
					{
						period: 'within 3-10 days of guest arrival'
					}
				]
			}
		}
	},
	[Channel.RENTALS_UNITED_TUI]: {
		id: Channel.RENTALS_UNITED_TUI,
		active: true,
		teesandcees: 'https://www.tui.co.uk/destinations/info/website-terms-and-conditions',
		commission: 'default_rate',
		faqs: [
			{
				title: `Where are the ${channelFriendlyNames[Channel.RENTALS_UNITED_TUI]} terms and conditions?`,
				text: 'You can read the terms and conditions here.',
				link: 'https://www.tui.co.uk/destinations/info/website-terms-and-conditions'
			}
		],
		policies: {
			[CancellationPreferenceTypes.FLEXIBLE]: {
				name: 'RELAXED',
				refund: [
					{
						amount: 100,
						period: 'up to 30 days before arrival'
					},
					{
						amount: 0,
						period: 'less than 30 days before arrival'
					}
				],
				guestPays: [
					{
						amount: 25,
						period: 'at time of booking'
					},
					{
						amount: 75,
						period: '60 days before arrival'
					}
				],
				ownerPaid: [
					{
						period: 'within 3-10 days of guest arrival'
					}
				]
			},
			[CancellationPreferenceTypes.MODERATE]: {
				name: 'MODERATE',
				refund: [
					{
						amount: 100,
						period: 'up to 30 days before arrival'
					},
					{
						amount: 0,
						period: 'less than 30 days before arrival'
					}
				],
				guestPays: [
					{
						amount: 25,
						period: 'at time of booking'
					},
					{
						amount: 75,
						period: '60 days before arrival'
					}
				],
				ownerPaid: [
					{
						period: 'within 3-10 days of guest arrival'
					}
				]
			},
			[CancellationPreferenceTypes.STRICT]: {
				name: 'STRICT',
				refund: [
					{
						amount: 100,
						period: 'up to 30 days before arrival'
					},
					{
						amount: 0,
						period: 'less than 30 days before arrival'
					}
				],
				guestPays: [
					{
						amount: 25,
						period: 'at time of booking'
					},
					{
						amount: 75,
						period: '60 days before arrival'
					}
				],
				ownerPaid: [
					{
						period: 'within 3-10 days of guest arrival'
					}
				]
			},
		},
	},
	[Channel.TRAVELNEST]: {
		id: Channel.TRAVELNEST,
		active: true,
		teesandcees: 'https://travelnest.freshdesk.com/support/solutions/articles/101000365056-travelnest-terms-and-conditions',
		commission: 'default_rate',
		faqs: [
			{
				title: `Where are the ${channelFriendlyNames[Channel.TRAVELNEST]} terms and conditions?`,
				text: 'You can read the terms and conditions here.',
				link: 'https://travelnest.freshdesk.com/support/solutions/articles/101000365056-travelnest-terms-and-conditions'
			}
		],
		policies: {
			[CancellationPreferenceTypes.STRICT]: {
				name: 'cancellation_policy_direct_strict_v1',
				refund: [
					{
						amount: 100,
						period: 'up to 30 days before arrival'
					}
				],
				guestPays: [
					{
						amount: 100,
						period: '30 days before arrival'
					}
				],
				ownerPaid: [
					{
						period: 'within 3-10 days of guest arrival'
					}
				]
			},
			[CancellationPreferenceTypes.MODERATE]: {
				name: 'cancellation_policy_direct_moderate_v1',
				refund: [
					{
						amount: 100,
						period: 'up to 14 days before arrival'
					}
				],
				guestPays: [
					{
						amount: 100,
						period: '14 days before arrival'
					}
				],
				ownerPaid: [
					{
						period: 'within 3-10 days of guest arrival'
					}
				]
			},
			[CancellationPreferenceTypes.FLEXIBLE]: {
				name: 'cancellation_policy_direct_flexible_v1',
				refund: [
					{
						amount: 100,
						period: 'up to 5 days before arrival'
					}
				],
				guestPays: [
					{
						amount: 100,
						period: '5 days before arrival'
					}
				],
				ownerPaid: [
					{
						period: 'within 3-10 days of guest arrival'
					}
				]
			}
		}
	}
	// tslint:disable-next-line:max-file-line-count
};
